import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, doc, getDocs, getDoc, addDoc, deleteDoc, updateDoc, serverTimestamp, query, orderBy, limit, startAfter } from 'firebase/firestore';
import LoadingIndicator from '../common/LoadingIndicator';
import CourseStatistics from './CourseStatistics';
import { runCertificateCategoryMigration } from '../../utils/migrateCertificatesToCourseCategory';

function CourseManagement() {
  const [courses, setCourses] = useState([]);
  const [courseName, setCourseName] = useState('');
  const [moduleData, setModuleData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1); // kept for compatibility with existing UI but unused for server pagination
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const coursesPerPage = 6;
  const [expandedCourses, setExpandedCourses] = useState({});
  const fileInputRefs = useRef({});
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    type: '',
    image: '',
    modules: 0,
    status: 'available',
    category: '',
    duration: '',
    rating: '0.0',
    prerequisites: '',
    learningObjectives: '',
    targetAudience: '',
    difficulty: 'beginner'
  });

  // New states for editing
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});

  // Course-level editing state
  const [courseEditingId, setCourseEditingId] = useState(null);
  const [courseEditData, setCourseEditData] = useState({});

  const normalizeCourseType = (value) => {
    if (value === true) return 'certification';
    if (value === false || value == null) return 'non-certification';
    let t = String(value).toLowerCase().trim();
    t = t.replace(/[_\s]+/g, '-');
    if (t === 'certification' || t === 'certifcation' || t.startsWith('cert')) return 'certification';
    return 'non-certification';
  };

  // Predefined categories, types, statuses, and difficulty levels
  const courseCategories = ['Arts and Humanities', 'Biology and Health', 'Business', 'Information Technology', 'Language Learning', 'Personal Development', 'Social Sciences', 'Teaching and Academics'];
  const courseTypes = [{ value: 'certification', label: 'Certification Course' }, { value: 'non-certification', label: 'Free Course' }];
  const courseStatuses = [{ value: 'available', label: 'Available' }, { value: 'coming_soon', label: 'Coming Soon' }, { value: 'archived', label: 'Archived' }, { value: 'enrolled', label: 'Enrolled' }, { value: 'completed', label: 'Completed' }];
  const difficultyLevels = [{ value: 'beginner', label: 'Beginner' }, { value: 'intermediate', label: 'Intermediate' }, { value: 'advanced', label: 'Advanced' }];

  // Maintenance state
  const [isRunningMigration, setIsRunningMigration] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const handleRunMigration = useCallback(async () => {
    if (isRunningMigration) return;
    try {
      setIsRunningMigration(true);
      setMigrationResult(null);
      const result = await runCertificateCategoryMigration();
      setMigrationResult(result);
    } catch (e) {
      console.error('Migration failed:', e);
      setMigrationResult({ error: e?.message || 'Unknown error' });
    } finally {
      setIsRunningMigration(false);
    }
  }, [isRunningMigration]);

  const loadInitialCourses = useCallback(async () => {
    try {
      setLoading(true);
      const coursesCol = collection(db, 'courses');
      const q = query(coursesCol, orderBy('title'), limit(coursesPerPage));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data(), modules: Array.isArray(d.data().modules) ? d.data().modules : [] }));
      setCourses(list);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === coursesPerPage);
    } catch (error) {
      console.error("Error loading courses:", error);
    } finally {
      setLoading(false);
    }
  }, [coursesPerPage]);

  const loadMoreCourses = useCallback(async () => {
    if (!hasMore || isLoadingMore || !lastVisible) return;
    try {
      setIsLoadingMore(true);
      const coursesCol = collection(db, 'courses');
      const q = query(coursesCol, orderBy('title'), startAfter(lastVisible), limit(coursesPerPage));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data(), modules: Array.isArray(d.data().modules) ? d.data().modules : [] }));
      setCourses(prev => [...prev, ...list]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || lastVisible);
      setHasMore(snapshot.docs.length === coursesPerPage);
    } catch (error) {
      console.error("Error loading more courses:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [coursesPerPage, hasMore, isLoadingMore, lastVisible]);

  useEffect(() => {
    loadInitialCourses();
  }, [loadInitialCourses]);

  const fetchCourseDetails = useCallback(async (courseId) => {
    try {
      const courseRef = doc(db, 'courses', courseId);
      const courseSnap = await getDoc(courseRef);
      return courseSnap.exists() ? { id: courseSnap.id, ...courseSnap.data() } : null;
    } catch (e) {
      console.error('Error fetching course details:', e);
      return null;
    }
  }, []);

  const deleteSubCourse = useCallback(async (courseId, moduleId, subCourseId) => {
    try {
      const courseRef = doc(db, 'courses', courseId);
      const courseDoc = await getDoc(courseRef);
      const courseData = courseDoc.data();
      const updatedModules = (courseData.modules || []).map(module => {
        if (module.moduleId === moduleId) {
          return { ...module, subCourses: (module.subCourses || []).filter(sub => sub.subCourseId !== subCourseId) };
        }
        return module;
      });
      await updateDoc(courseRef, { modules: updatedModules, lastUpdated: serverTimestamp() });
      // optimistic local update
      setCourses(prev => prev.map(c => c.id === courseId ? { ...c, modules: updatedModules } : c));
    } catch (error) {
      console.error("Error deleting sub-course:", error);
    }
  }, []);

  const toggleEdit = (courseId, moduleId, subCourse) => {
    setEditing({ courseId, moduleId, subCourseId: subCourse.subCourseId });
    setEditData({ ...subCourse });
  };

  const handleEditChange = (e, field) => {
    if (field === 'quizData') {
      // Don't set the File object directly
      return;
    }
    setEditData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const startCourseEdit = (course) => {
    setCourseEditingId(course.id);
    setCourseEditData({
      title: course.title || '',
      description: course.description || '',
      type: normalizeCourseType(course.type || ''),
      image: course.image || '',
      modules: typeof course.modules === 'number' ? course.modules : (Array.isArray(course.modules) ? course.modules.length : 0),
      status: course.status || 'available',
      category: course.category || '',
      duration: course.duration || '',
      rating: typeof course.rating === 'number' ? course.rating : Number(course.rating || 0),
      prerequisites: course.prerequisites || '',
      learningObjectives: course.learningObjectives || '',
      targetAudience: course.targetAudience || '',
      difficulty: course.difficulty || 'beginner'
    });
    setUploadError(null);
    setUploadSuccess(false);
  };

  const handleCourseEditChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'type' ? normalizeCourseType(value) : value;
    setCourseEditData(prev => ({ ...prev, [name]: nextValue }));
  };

  const saveCourseEdit = useCallback(async () => {
    if (!courseEditingId) return;
    try {
      setLoading(true);
      const courseRef = doc(db, 'courses', courseEditingId);
      const payload = {
        title: courseEditData.title || '',
        description: courseEditData.description || '',
        type: normalizeCourseType(courseEditData.type),
        image: courseEditData.image || '',
        // Preserve existing modules array if present; only store number when it's numeric-based schema
        modules: Number(courseEditData.modules) || 0,
        status: courseEditData.status || 'available',
        category: courseEditData.category || '',
        duration: courseEditData.duration || '',
        rating: Number(courseEditData.rating) || 0,
        prerequisites: courseEditData.prerequisites || '',
        learningObjectives: courseEditData.learningObjectives || '',
        targetAudience: courseEditData.targetAudience || '',
        difficulty: courseEditData.difficulty || 'beginner',
        lastUpdated: serverTimestamp()
      };

      await updateDoc(courseRef, payload);

      // Optimistic local update (do not overwrite modules array if it exists locally)
      setCourses(prev => prev.map(c => {
        if (c.id !== courseEditingId) return c;
        const next = { ...c, ...payload };
        if (Array.isArray(c.modules)) {
          next.modules = c.modules; // keep array; modules number change is for metadata only
        }
        return next;
      }));

      setUploadSuccess('Course updated successfully!');
      setCourseEditingId(null);
    } catch (e) {
      console.error('Error updating course:', e);
      setUploadError('Failed to update course. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [courseEditingId, courseEditData]);

  const cancelCourseEdit = () => {
    setCourseEditingId(null);
    setCourseEditData({});
    setUploadError(null);
    setUploadSuccess(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      setUploadError("Please upload a JSON file");
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const quizData = JSON.parse(evt.target.result);
          
          // Validate quiz data structure
          if (!quizData.questions || !Array.isArray(quizData.questions)) {
            throw new Error("Invalid quiz format. Must contain questions array.");
          }

          setEditData(prev => ({ ...prev, quizData: quizData }));
        } catch (error) {
          console.error("Error processing quiz file:", error);
          setUploadError(`Failed to process the quiz file: ${error.message}`);
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error("Error reading file:", error);
      setUploadError(`Failed to read the file: ${error.message}`);
    }
  };

  const saveEdit = useCallback(async (courseId, moduleId) => {
    try {
      const courseRef = doc(db, 'courses', courseId);
      const courseData = (await getDoc(courseRef)).data();
      const updatedModules = (courseData.modules || []).map(module => {
        if (module.moduleId === moduleId) {
          return {
            ...module,
            subCourses: (module.subCourses || []).map(sub =>
              sub.subCourseId === editing.subCourseId
                ? { ...sub, title: editData.title, description: editData.description, presentationLink: editData.presentationLink, quizData: editData.quizData || sub.quizData }
                : sub
            )
          };
        }
        return module;
      });
      await updateDoc(courseRef, { modules: updatedModules, lastUpdated: serverTimestamp() });
      setEditing(null);
      setUploadSuccess("Changes saved successfully!");
      // optimistic local update
      setCourses(prev => prev.map(c => c.id === courseId ? { ...c, modules: updatedModules } : c));
    } catch (error) {
      console.error("Error saving edit:", error);
      setUploadError("Failed to save changes. Please try again.");
    }
  }, [editData, editing]);

  /*const handleNewCourseChange = (e) => {
    const { name, value } = e.target;
    setNewCourse(prev => ({
      ...prev,
      [name]: value
    }));
  };*/

  const addCourse = useCallback(async () => {
    if (!newCourse.title || !newCourse.category) {
      setUploadError("Course title and category are required.");
      return;
    }

    setLoading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const created = await addDoc(collection(db, 'courses'), {
        ...newCourse,
        type: normalizeCourseType(newCourse.type),
        createdAt: serverTimestamp(),
        modules: Number(newCourse.modules) || 0,
        rating: Number(newCourse.rating) || 0.0,
      });

      setNewCourse({
        title: '',
        description: '',
        type: '',
        image: '',
        modules: 0,
        status: 'available',
        category: '',
        duration: '',
        rating: '0.0',
        prerequisites: '',
        learningObjectives: '',
        targetAudience: '',
        difficulty: 'beginner'
      });

      setUploadSuccess(true);
      // optimistic append at top (may be slightly off due to orderBy('title'))
      setCourses(prev => [{ id: created.id, ...newCourse, modules: [], rating: Number(newCourse.rating) || 0.0 }, ...prev]);
    } catch (error) {
      console.error("Error adding course:", error);
      setUploadError("Failed to add the course. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [newCourse]);

  const deleteCourse = useCallback(async (courseId) => {
    try {
      await deleteDoc(doc(db, 'courses', courseId));
      setCourses(prev => prev.filter(course => course.id !== courseId));
      setUploadSuccess("Course deleted successfully!");
    } catch (error) {
      console.error("Error deleting course:", error);
      setUploadError("Failed to delete the course. Please try again.");
    }
  }, []);

  const handleInputChange = (e, courseId, field) => {
    setModuleData(prev => ({
      ...prev,
      [courseId]: {
        ...prev[courseId],
        [field]: e.target.value
      }
    }));
  };

  const handleQuizUpload = async (e, courseId, moduleId, subCourseId) => {
    setUploadError(null);
    setUploadSuccess(false);
    const file = e.target.files[0];
    
    if (!file) {
      setUploadError("Please select a file.");
      return;
    }

    if (file.type !== 'application/json') {
      setUploadError("Please upload a JSON file");
      return;
    }

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const quizData = JSON.parse(evt.target.result);
          
          // Validate quiz data structure
          if (!quizData.questions || !Array.isArray(quizData.questions)) {
            throw new Error("Invalid quiz format. Must contain questions array.");
          }

          // If this is for a new sub-course (no subCourseId provided), just store it in moduleData
          if (!subCourseId || courseId && !moduleId) {
            setModuleData(prev => ({
              ...prev,
              [courseId]: {
                ...prev[courseId],
                quizData: quizData
              }
            }));
            setUploadSuccess("Quiz loaded successfully! Click 'Add Sub-course' to save.");
          } else {
            // This is for an existing sub-course, update it directly
            const courseRef = doc(db, 'courses', courseId);
            const courseDoc = await getDoc(courseRef);
            
            if (courseDoc.exists()) {
              const courseData = courseDoc.data();
              const updatedModules = courseData.modules.map(module => {
                if (module.moduleId === moduleId) {
                  return {
                    ...module,
                    subCourses: module.subCourses.map(subCourse => {
                      if (subCourse.subCourseId === subCourseId) {
                        return {
                          ...subCourse,
                          quizData: quizData
                        };
                      }
                      return subCourse;
                    })
                  };
                }
                return module;
              });

              await updateDoc(courseRef, { 
                modules: updatedModules,
                lastUpdated: serverTimestamp()
              });

              setUploadSuccess("Quiz uploaded successfully!");
              // optimistic local update
              setCourses(prev => prev.map(c => {
                if (c.id !== courseId) return c;
                const updatedModulesLocal = c.modules.map(m => m.moduleId === moduleId ? { ...m, subCourses: m.subCourses.map(sc => sc.subCourseId === subCourseId ? { ...sc, quizData } : sc) } : m);
                return { ...c, modules: updatedModulesLocal };
              }));
            }
          }
        } catch (error) {
          console.error("Error processing quiz file:", error);
          setUploadError(`Failed to process the quiz file: ${error.message}`);
        }
        setLoading(false);
      };

      reader.readAsText(file);
    } catch (error) {
      console.error("Error processing file:", error);
      setUploadError(`Failed to process the file: ${error.message}`);
      setLoading(false);
    }
  };

  const handleNewCourseChange = (e) => {
    const { name, value } = e.target;
    setNewCourse(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const addSubCourse = useCallback(async (courseId) => {
    const moduleInfo = moduleData[courseId];
  
    // Ensure required fields are provided
    if (!moduleInfo.moduleId || !moduleInfo.subCourseId || !moduleInfo.title) {
      setUploadError("Please fill in all required fields (Module ID, Sub-course ID, and Title)");
      return;
    }
  
    setLoading(true);
    setUploadError(null);
    setUploadSuccess(false);
  
    try {
      const courseRef = doc(db, 'courses', courseId);
      const courseDoc = await getDoc(courseRef);
  
      if (!courseDoc.exists()) {
        throw new Error("Course not found");
      }
  
      const courseData = courseDoc.data();
      let modules = courseData.modules || [];
  
      if (typeof modules === 'number') {
        modules = [];
      }
  
      let moduleIndex = modules.findIndex(m => m.moduleId === moduleInfo.moduleId);
      const currentTime = new Date().toISOString();
  
      if (moduleIndex === -1) {
        modules.push({
          moduleId: moduleInfo.moduleId,
          createdAt: currentTime,
          subCourses: []
        });
        moduleIndex = modules.length - 1;
      }
  
      const subCourseExists = modules[moduleIndex].subCourses?.some(
        sc => sc.subCourseId === moduleInfo.subCourseId
      );
  
      if (subCourseExists) {
        throw new Error("Sub-course ID already exists in this module");
      }
  
      const newSubCourse = {
        subCourseId: moduleInfo.subCourseId,
        title: moduleInfo.title,
        description: moduleInfo.description || '',
        presentationLink: moduleInfo.presentationLink || '',
        quizData: moduleInfo.quizData || null,
        createdAt: currentTime
      };
  
      if (!modules[moduleIndex].subCourses) {
        modules[moduleIndex].subCourses = [];
      }
  
      modules[moduleIndex].subCourses.push(newSubCourse);
  
      await updateDoc(courseRef, {
        modules: modules,
        lastUpdated: serverTimestamp()
      });
  
      // Reset the form after successful submission
      setModuleData(prev => ({
        ...prev,
        [courseId]: {
          moduleId: '',
          subCourseId: '',
          title: '',
          description: '',
          presentationLink: '',
          quizData: null
        }
      }));
  
      if (fileInputRefs.current[courseId]) {
        fileInputRefs.current[courseId].value = '';
      }
  
      setUploadSuccess("Sub-course added successfully!");
      // optimistic local update
      setCourses(prev => prev.map(c => c.id === courseId ? { ...c, modules } : c));
    } catch (error) {
      console.error("Error adding sub-course:", error);
      setUploadError(`Failed to add the sub-course: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [moduleData]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const filteredCourses = courses.filter(course =>
    course.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const lastCourseIndex = currentPage * coursesPerPage;
  const firstCourseIndex = lastCourseIndex - coursesPerPage;
  const currentCourses = filteredCourses.slice(firstCourseIndex, lastCourseIndex);
  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

  const changePage = () => {};

  const toggleCourseExpansion = useCallback(async (courseId) => {
    setExpandedCourses(prev => ({ ...prev, [courseId]: !prev[courseId] }));
    // if expanding, load fresh details
    const willExpand = !expandedCourses[courseId];
    if (willExpand) {
      const details = await fetchCourseDetails(courseId);
      if (details) {
        setCourses(prev => prev.map(c => c.id === courseId ? { ...c, ...details, modules: Array.isArray(details.modules) ? details.modules : [] } : c));
      }
    }
  }, [expandedCourses, fetchCourseDetails]);

  const updateCourseStatus = useCallback(async (courseId, newStatus) => {
    try {
      const courseRef = doc(db, 'courses', courseId);
      await updateDoc(courseRef, { status: newStatus, lastUpdated: serverTimestamp() });
      setUploadSuccess("Course status updated successfully!");
      // optimistic local update
      setCourses(prev => prev.map(c => c.id === courseId ? { ...c, status: newStatus } : c));
    } catch (error) {
      console.error("Error updating course status:", error);
      setUploadError("Failed to update course status. Please try again.");
    }
  }, []);

  const QuizUploader = useMemo(() => function QuizUploader({ onUpload }) {
    const onChange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const quiz = JSON.parse(reader.result);
          if (!quiz?.questions || !Array.isArray(quiz.questions)) throw new Error('Invalid quiz format');
          onUpload(quiz);
        } catch (err) {
          console.error('Invalid quiz format', err);
          setUploadError('Invalid quiz format');
        }
      };
      reader.readAsText(file);
    };
    return <input type="file" accept=".json" onChange={onChange} className="w-full" />;
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-8">Course Management</h2>

      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          {/* Course Statistics */}
          <CourseStatistics />

          {/* Maintenance tools */}
          <div className="bg-white rounded-lg shadow-lg p-4 mb-8">
            <h3 className="text-lg font-semibold mb-3">Maintenance</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRunMigration}
                disabled={isRunningMigration}
                className="bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,68,0)] transition-colors disabled:opacity-50"
              >
                {isRunningMigration ? 'Running Migration…' : 'Run Certificate Migration'}
              </button>
              {migrationResult && !migrationResult.error && (
                <span className="text-sm text-gray-700">
                  Updated: {migrationResult.updated || 0}, Deleted duplicates: {migrationResult.deleted || 0}
                </span>
              )}
              {migrationResult?.error && (
                <span className="text-sm text-red-600">{migrationResult.error}</span>
              )}
            </div>
          </div>

          {/* Add New Course Form */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-6">Add New Course</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={newCourse.title}
                    onChange={handleNewCourseChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    placeholder="Enter course title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    name="category"
                    value={newCourse.category}
                    onChange={handleNewCourseChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    required
                  >
                    <option value="">Select a category</option>
                    {courseCategories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course Type</label>
                  <select
                    name="type"
                    value={newCourse.type}
                    onChange={handleNewCourseChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  >
                    {courseTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    name="status"
                    value={newCourse.status}
                    onChange={handleNewCourseChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  >
                    {courseStatuses.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Additional Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Modules</label>
                  <input
                    type="number"
                    name="modules"
                    value={newCourse.modules}
                    onChange={handleNewCourseChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                  <input
                    type="text"
                    name="duration"
                    value={newCourse.duration}
                    onChange={handleNewCourseChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    placeholder="e.g., 2 hours"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty Level</label>
                  <select
                    name="difficulty"
                    value={newCourse.difficulty}
                    onChange={handleNewCourseChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  >
                    {difficultyLevels.map((level) => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                  <input
                    type="text"
                    name="image"
                    value={newCourse.image}
                    onChange={handleNewCourseChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    placeholder="Enter image URL"
                  />
                </div>
              </div>
            </div>

            {/* Full Width Fields */}
            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={newCourse.description}
                  onChange={handleNewCourseChange}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  rows="3"
                  placeholder="Enter course description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prerequisites</label>
                <textarea name="prerequisites"
                  value={newCourse.prerequisites}
                  onChange={handleNewCourseChange}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  rows="2"
                  placeholder="Enter course prerequisites"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Learning Objectives</label>
                <textarea
                  name="learningObjectives"
                  value={newCourse.learningObjectives}
                  onChange={handleNewCourseChange}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  rows="2"
                  placeholder="Enter learning objectives"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <textarea
                  name="targetAudience"
                  value={newCourse.targetAudience}
                  onChange={handleNewCourseChange}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  rows="2"
                  placeholder="Describe the target audience"
                />
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={addCourse}
                disabled={loading}
                className="w-full bg-[rgb(130,88,18)] text-white py-3 px-4 rounded-md hover:bg-[rgb(110,68,0)] transition-colors disabled:opacity-50"
              >
                {loading ? 'Adding Course...' : 'Add Course'}
              </button>

              {uploadError && (
                <p className="mt-2 text-red-600">{uploadError}</p>
              )}
              {uploadSuccess && (
                <p className="mt-2 text-green-600">Course added successfully!</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <input
              type="text"
              value={courseName}
              onChange={e => setCourseName(e.target.value)}
              placeholder="New Course Name"
              className="border p-2 rounded w-full mb-3"
            />

            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search Courses"
              className="border p-2 rounded w-full mb-3"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses
              .filter(course =>
                course.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                course.description?.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map(course => (
              <div key={course.id} className="bg-white rounded-lg shadow-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">{course.title}</h3>
                  <div>
                    <select
                      value={course.status || 'available'}
                      onChange={(e) => updateCourseStatus(course.id, e.target.value)}
                      className="mr-4 p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    >
                      {courseStatuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>

                    {courseEditingId === course.id ? (
                      <>
                        <button
                          onClick={saveCourseEdit}
                          className="text-green-600 hover:text-green-800 mr-4"
                        >
                          Save Course
                        </button>
                        <button
                          onClick={cancelCourseEdit}
                          className="text-gray-600 hover:text-gray-800 mr-4"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => startCourseEdit(course)}
                        className="text-blue-600 hover:text-blue-800 mr-4"
                      >
                        Edit Course
                      </button>
                    )}

                    <button
                      onClick={() => toggleCourseExpansion(course.id)}
                      className="text-blue-600 hover:text-blue-800 mr-4"
                    >
                      {expandedCourses[course.id] ? 'Hide Details' : 'Show Details'}
                    </button>

                    <button
                      onClick={() => deleteCourse(course.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete Course
                    </button>
                  </div>
                </div>

                {expandedCourses[course.id] && (
                  <div className="mt-4">
                    {courseEditingId === course.id && (
                      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                          <input type="text" name="title" value={courseEditData.title || ''} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <select name="category" value={courseEditData.category || ''} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]">
                            <option value="">Select a category</option>
                            {courseCategories.map((category) => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Course Type</label>
                          <select name="type" value={courseEditData.type || ''} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]">
                            {courseTypes.map((type) => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <select name="status" value={courseEditData.status || 'available'} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]">
                            {courseStatuses.map((status) => (
                              <option key={status.value} value={status.value}>{status.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Number of Modules</label>
                          <input type="number" name="modules" value={courseEditData.modules || 0} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]" min="0" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                          <input type="text" name="duration" value={courseEditData.duration || ''} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]" placeholder="e.g., 2 hours" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                          <select name="difficulty" value={courseEditData.difficulty || 'beginner'} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]">
                            {difficultyLevels.map((level) => (
                              <option key={level.value} value={level.value}>{level.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                          <input type="text" name="image" value={courseEditData.image || ''} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]" placeholder="Enter image URL" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <textarea name="description" value={courseEditData.description || ''} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]" rows="3" placeholder="Enter course description" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Prerequisites</label>
                          <textarea name="prerequisites" value={courseEditData.prerequisites || ''} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]" rows="2" placeholder="Enter course prerequisites" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Learning Objectives</label>
                          <textarea name="learningObjectives" value={courseEditData.learningObjectives || ''} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]" rows="2" placeholder="Enter learning objectives" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                          <textarea name="targetAudience" value={courseEditData.targetAudience || ''} onChange={handleCourseEditChange} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]" rows="2" placeholder="Describe the target audience" />
                        </div>
                      </div>
                    )}
                    {course.modules.map(module => (
                      <div key={module.moduleId} className="mb-2">
                        <h4 className="text-lg font-medium">Module {module.moduleId}</h4>
                        {module.subCourses.map(subCourse => (
                          <div key={subCourse.subCourseId} className="mb-2 border-b pb-2">
                            {editing && editing.courseId === course.id && editing.moduleId === module.moduleId && editing.subCourseId === subCourse.subCourseId ? (
                              <>
                                <input
                                  type="text"
                                  value={editData.title || ''}
                                  onChange={(e) => handleEditChange(e, 'title')}
                                  className="w-full p-2 border rounded-md mb-2"
                                  placeholder="Title"
                                />
                                <textarea
                                  value={editData.description || ''}
                                  onChange={(e) => handleEditChange(e, 'description')}
                                  className="w-full p-2 border rounded-md mb-2"
                                  placeholder="Description"
                                />
                                <input
                                  type="text"
                                  value={editData.presentationLink || ''}
                                  onChange={(e) => handleEditChange(e, 'presentationLink')}
                                  className="w-full p-2 border rounded-md mb-2"
                                  placeholder="Presentation Link"
                                />
                                <div className="mb-2">
                                  <p className="text-sm mb-1">Current Quiz Status: {editData.quizData ? 'Available' : 'Not Available'}</p>
                                  <input
                                    type="file"
                                    accept=".json"
                                    onChange={handleFileChange}
                                    className="w-full mb-2"
                                  />
                                </div>
                                {uploadError && <p className="text-red-500 text-sm mb-2">{uploadError}</p>}
                                {uploadSuccess && <p className="text-green-500 text-sm mb-2">{uploadSuccess}</p>}
                                <button
                                  onClick={() => saveEdit(course.id, module.moduleId)}
                                  className="bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,68,0)] transition-colors mr-2"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => {
                                    setEditing(null);
                                    setUploadError(null);
                                    setUploadSuccess(false);
                                  }}
                                  className="bg-gray-400 text-white py-2 px-4 rounded-md hover:bg-gray-500 transition-colors"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <h5 className="font-semibold">{subCourse.title}</h5>
                                <p>ID: {subCourse.subCourseId}</p>
                                {subCourse.description && <p>Description: {subCourse.description}</p>}
                                
                                {/* Add Quiz Status Display */}
                                <div className="mt-2 mb-2">
                                  <p className={`text-sm ${subCourse.quizData ? 'text-green-600' : 'text-red-600'}`}>
                                    Quiz Status: {subCourse.quizData ? 'Available' : 'Not Available'}
                                  </p>
                                </div>

                                {/* Add Quiz Upload Section */}
                                {!subCourse.quizData && (
                                  <div className="mt-2 mb-2">
                                    <div className="bg-gray-50 p-4 rounded-md mb-4">
                                      <h4 className="font-medium text-gray-800 mb-2">Quiz JSON Format:</h4>
                                      <pre className="text-sm text-gray-600 whitespace-pre-wrap">
{`{
  "questions": [
    {
      "question": "Your question text here",
      "options": [
        "Option 1",
        "Option 2",
        "Option 3",
        "Option 4"
      ],
      "correctAnswer": 0  // Index of correct option (0-3)
    }
  ]
}`}
                                      </pre>
                                      <p className="text-sm text-gray-600 mt-2">
                                        • Each question must have a question text, 4 options, and a correctAnswer index (0-3)<br/>
                                        • The correctAnswer must be a number between 0 and 3, corresponding to the index of the correct option<br/>
                                        • All questions and options must be strings<br/>
                                        • A minimum of 1 question is required
                                      </p>
                                    </div>
                                    <QuizUploader onUpload={(quiz) => {
                                      // reuse existing updater to persist
                                      const fakeEvent = { target: { files: [new Blob([JSON.stringify(quiz)], { type: 'application/json' })] } };
                                      handleQuizUpload(fakeEvent, course.id, module.moduleId, subCourse.subCourseId);
                                    }} />
                                  </div>
                                )}

                                <button
                                  onClick={() => toggleEdit(course.id, module.moduleId, subCourse)}
                                  className="bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,68,0)] transition-colors mr-2"
                                >
                                  Edit
                                </button>

                                <button
                                  onClick={() => deleteSubCourse(course.id, module.moduleId, subCourse.subCourseId)}
                                  className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                    <h4 className="text-lg font-medium mb-3">Add New Sub-course:</h4>
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={moduleData[course.id]?.moduleId || ''}
                        onChange={(e) => handleInputChange(e, course.id, 'moduleId')}
                        placeholder="Module ID"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      />
                      <input
                        type="text"
                        value={moduleData[course.id]?.subCourseId || ''}
                        onChange={(e) => handleInputChange(e, course.id, 'subCourseId')}
                        placeholder="Sub-course ID"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      />
                      <input
                        type="text"
                        value={moduleData[course.id]?.title || ''}
                        onChange={(e) => handleInputChange(e, course.id, 'title')}
                        placeholder="Sub-course Title"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      />
                      <textarea
                        value={moduleData[course.id]?.description || ''}
                        onChange={(e) => handleInputChange(e, course.id, 'description')}
                        placeholder="Sub-course Description"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                        rows="3"
                      />
                      <input
                        type="text"
                        value={moduleData[course.id]?.presentationLink || ''}
                        onChange={(e) => handleInputChange(e, course.id, 'presentationLink')}
                        placeholder="Presentation Link"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quiz File (JSON format only)
                        </label>
                        <div className="bg-gray-50 p-4 rounded-md mb-4">
                          <h4 className="font-medium text-gray-800 mb-2">Quiz JSON Format:</h4>
                          <pre className="text-sm text-gray-600 whitespace-pre-wrap">
{`{
  "questions": [
    {
      "question": "Your question text here",
      "options": [
        "Option 1",
        "Option 2",
        "Option 3",
        "Option 4"
      ],
      "correctAnswer": 0  // Index of correct option (0-3)
    }
  ]
}`}
                          </pre>
                          <p className="text-sm text-gray-600 mt-2">
                            • Each question must have a question text, 4 options, and a correctAnswer index (0-3)<br/>
                            • The correctAnswer must be a number between 0 and 3, corresponding to the index of the correct option<br/>
                            • All questions and options must be strings<br/>
                            • A minimum of 1 question is required
                          </p>
                        </div>
                        <QuizUploader onUpload={(quiz) => {
                          setModuleData(prev => ({
                            ...prev,
                            [course.id]: { ...(prev[course.id] || {}), quizData: quiz }
                          }));
                          setUploadSuccess("Quiz loaded successfully! Click 'Add Sub-course' to save.");
                        }} />
                      </div>
                      <button
                        onClick={() => addSubCourse(course.id)}
                        className="w-full bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,68,0)] transition-colors"
                      >
                        Add Sub-course
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-6">
              <button onClick={loadMoreCourses} className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400" disabled={isLoadingMore}>
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {uploadError && (
        <div className="text-red-500 mt-4">{uploadError}</div>
      )}
      
      {uploadSuccess && (
        <div className="text-green-500 mt-4">Upload successful!</div>
      )}
    </div>
  );
}

export default CourseManagement;